/**
 * Conciliación de Control de pagos contra los lotes de pago del banco.
 * [NUEVO 03/11/2026]
 *
 * Lógica pura (sin React), para poder testearla: qué dice la columna "Pago
 * banco" de cada fila y qué filas entran en cada filtro de alerta.
 *
 * Ojo con el período en que esto tiene sentido: el lote se importa ANTES de
 * registrar el pago en SAP. En cuanto se registra, las facturas se cierran y
 * salen de la bandeja — así que "Pagada por banco" se ve entre la importación
 * y el registro en SAP, que es justamente la ventana para controlar.
 */
import type { EstadoCruceBanco, FacturaParaPagar, LoteBanco, PagoBanco } from "../types/reviewItem";
import { formatFecha, formatMonto } from "./formato";
import { vinculoDeImportacion, type VinculoImportacion } from "./lotesPago";

export type FiltroBanco = "todas" | "pagadas" | "pagadas_sin_habilitar" | "habilitadas_sin_pago" | "con_diferencia";

export const TABS_BANCO: Array<{ clave: FiltroBanco; etiqueta: string; ayuda: string }> = [
  { clave: "todas", etiqueta: "Todas", ayuda: "Sin filtrar por pago en el banco." },
  {
    clave: "pagadas",
    etiqueta: "Pagadas por banco",
    ayuda: "La factura está identificada en un lote de pago importado.",
  },
  {
    clave: "pagadas_sin_habilitar",
    etiqueta: "Pagadas sin habilitar",
    ayuda: "Se mandó a pagar al banco pero no está marcada como habilitada para pagar.",
  },
  {
    clave: "habilitadas_sin_pago",
    etiqueta: "Habilitadas sin pago",
    ayuda: "Está habilitada para pagar y no aparece en ningún lote importado (ni la factura ni el proveedor).",
  },
  {
    clave: "con_diferencia",
    etiqueta: "Con diferencia",
    ayuda: "El importe pagado no coincide con el saldo de las facturas de esa línea del banco.",
  },
];

function pagoExacto(f: FacturaParaPagar): PagoBanco | null {
  return f.pago_banco && f.pago_banco.tipo === "factura" ? f.pago_banco : null;
}

export function aplicarFiltroBanco(items: FacturaParaPagar[], filtro: FiltroBanco): FacturaParaPagar[] {
  switch (filtro) {
    case "pagadas":
      return items.filter((f) => pagoExacto(f) !== null);
    case "pagadas_sin_habilitar":
      return items.filter((f) => pagoExacto(f) !== null && !f.pago_habilitado);
    case "habilitadas_sin_pago":
      // Si no se pudo consultar, no se afirma que falte el pago.
      return items.filter((f) => f.pago_habilitado && f.pago_banco_disponible === true && !f.pago_banco);
    case "con_diferencia":
      return items.filter((f) => pagoExacto(f)?.estado_cruce === "DIFERENCIA");
    default:
      return items;
  }
}

export type TonoPago = "ok" | "alerta" | "info" | "neutro";

/** Lo que muestra la celda "Pago banco". Nunca deduce: si no se pudo
 *  consultar lo dice, y un pago atribuido solo al proveedor no se presenta
 *  como pago de esta factura. */
export function textoPagoBanco(f: FacturaParaPagar): { texto: string; ayuda: string; tono: TonoPago } {
  if (f.pago_banco_disponible === false) {
    return { texto: "No disponible", ayuda: "No se pudieron consultar los lotes del banco.", tono: "neutro" };
  }
  const p = f.pago_banco;
  if (!p) return { texto: "—", ayuda: "Sin pago en los lotes importados.", tono: "neutro" };

  const fecha = formatFecha(p.fecha);
  const lote = `Lote ${p.id_transaccion} (${p.estado_lote ?? "estado sin dato"})`;
  if (p.tipo === "proveedor") {
    return {
      texto: `Proveedor en lote ${fecha}`,
      ayuda: `${lote}. El pago al proveedor no detalla esta factura (${descripcionCruce(p.estado_cruce)}).`,
      tono: "info",
    };
  }
  if (p.estado_cruce === "DIFERENCIA") {
    return {
      texto: `Pagada ${fecha} · dif. ${p.diferencia ?? ""}`.trim(),
      ayuda: `${lote}. Lo pagado no coincide con el saldo de las facturas de esa línea (diferencia ${p.diferencia} ${p.moneda ?? ""}).`,
      tono: "alerta",
    };
  }
  return {
    texto: `Pagada ${fecha}`,
    ayuda: `${lote}. ${descripcionCruce(p.estado_cruce)}.`,
    tono: p.estado_cruce === "CRUZADA" ? "ok" : "info",
  };
}

export function descripcionCruce(estado: EstadoCruceBanco): string {
  switch (estado) {
    case "CRUZADA":
      return "Facturas identificadas, el importe coincide";
    case "DIFERENCIA":
      return "Facturas identificadas, el importe no coincide";
    case "PARCIAL":
      return "Alguna factura de la referencia no se pudo identificar";
    case "SIN_DETALLE":
      return "La referencia del banco no lista facturas";
    case "SIN_PROVEEDOR":
      return "No se pudo identificar al proveedor";
  }
}

export const ETIQUETA_CRUCE: Record<EstadoCruceBanco, string> = {
  CRUZADA: "Cruzada",
  DIFERENCIA: "Diferencia",
  PARCIAL: "Parcial",
  SIN_DETALLE: "Sin detalle",
  SIN_PROVEEDOR: "Sin proveedor",
};

export const CLASE_CRUCE: Record<EstadoCruceBanco, string> = {
  CRUZADA: "badge-auth",
  DIFERENCIA: "badge-rej",
  PARCIAL: "badge-pend",
  SIN_DETALLE: "badge-wait",
  SIN_PROVEEDOR: "badge-rej",
};

/** Cantidad de pagos por estado de cruce, en el orden en que conviene leerlos. */
export function resumenCruce(lote: LoteBanco): Array<{ estado: EstadoCruceBanco; cantidad: number }> {
  const orden: EstadoCruceBanco[] = ["CRUZADA", "DIFERENCIA", "PARCIAL", "SIN_DETALLE", "SIN_PROVEEDOR"];
  const conteo = new Map<EstadoCruceBanco, number>();
  for (const l of lote.lineas) conteo.set(l.estado_cruce, (conteo.get(l.estado_cruce) ?? 0) + 1);
  return orden.filter((e) => conteo.has(e)).map((estado) => ({ estado, cantidad: conteo.get(estado)! }));
}

/** Texto del resultado de una importación, para el aviso. */
export function mensajeImportacion(r: {
  nuevo: boolean;
  estado: string | null;
  estado_anterior?: string | null;
  cantidad_pagos?: number;
  por_estado?: Record<string, number>;
}): string {
  if (!r.nuevo) {
    return r.estado_anterior && r.estado_anterior !== r.estado
      ? `El lote ya estaba importado: se actualizó de "${r.estado_anterior}" a "${r.estado}".`
      : "El lote ya estaba importado: se actualizaron el comprobante y las firmas.";
  }
  const por = r.por_estado ?? {};
  const partes = (Object.keys(ETIQUETA_CRUCE) as EstadoCruceBanco[])
    .filter((e) => por[e])
    .map((e) => `${por[e]} ${ETIQUETA_CRUCE[e].toLowerCase()}`);
  return `Lote importado: ${r.cantidad_pagos ?? 0} pagos (${partes.join(", ")}).`;
}

// ---------------------------------------------------------------------------
// [NUEVO 04/11/2026] Carga de varios PDFs, quitar un lote y filtro por fecha
// ---------------------------------------------------------------------------

/** Tope de archivos por carga múltiple: cada uno consulta SAP en el backend. */
export const MAX_ARCHIVOS_LOTE = 20;

/** Tope del backend con filtro de fechas (LOTES_BANCO_TOPE_FILTRO). */
export const TOPE_LOTES_CON_FILTRO = 200;

export type ResultadoArchivoLote = {
  archivo: string;
  resultado: "importado" | "actualizado" | "error";
  mensaje: string;
  /** [NUEVO 04/11/2026] Solo con `lotes_pago` prendida: para vincular el PDF a un lote de la app. */
  vinculo?: VinculoImportacion;
};

type RespuestaImportacion = Parameters<typeof mensajeImportacion>[0] & Parameters<typeof vinculoDeImportacion>[0];

/**
 * Importa los archivos UNO POR VEZ (cada uno consulta SAP en el backend) y
 * devuelve un resultado por archivo, en el mismo orden. Un archivo con error
 * —rechazado antes de subir o por el backend— no corta los demás.
 * Lógica pura: la página le pasa la llamada a la API.
 */
export async function importarEnSecuencia<F extends { name: string; size: number; type: string }>(
  files: F[],
  importar: (file: F) => Promise<RespuestaImportacion>,
  onProgreso?: (actual: number, total: number) => void
): Promise<ResultadoArchivoLote[]> {
  const resultados: ResultadoArchivoLote[] = [];
  for (const [i, file] of files.entries()) {
    onProgreso?.(i + 1, files.length);
    const motivo = motivoRechazoLote(file);
    if (motivo) {
      resultados.push({ archivo: file.name, resultado: "error", mensaje: motivo });
      continue;
    }
    try {
      const r = await importar(file);
      const vinculo = vinculoDeImportacion(r);
      resultados.push({
        archivo: file.name,
        resultado: r.nuevo ? "importado" : "actualizado",
        mensaje: mensajeImportacion(r),
        // Con el flag apagado la clave ni existe: el resultado queda igual que antes.
        ...(vinculo ? { vinculo } : {}),
      });
    } catch (err) {
      resultados.push({
        archivo: file.name,
        resultado: "error",
        mensaje: err instanceof Error ? err.message : "No se pudo importar el lote.",
      });
    }
  }
  return resultados;
}

/** null si la cantidad de archivos elegidos se puede importar. */
export function motivoDemasiadosArchivos(cantidad: number): string | null {
  return cantidad > MAX_ARCHIVOS_LOTE ? `Se pueden importar hasta ${MAX_ARCHIVOS_LOTE} archivos por vez.` : null;
}

/** Primera línea del resumen de una carga múltiple. */
export function tituloResumenImportacion(resultados: ResultadoArchivoLote[]): string {
  const cuenta = (r: ResultadoArchivoLote["resultado"]) => resultados.filter((x) => x.resultado === r).length;
  const partes = [
    [cuenta("importado"), "importado", "importados"],
    [cuenta("actualizado"), "ya estaba importado", "ya estaban importados"],
    [cuenta("error"), "con error", "con error"],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, uno, varios]) => `${n} ${n === 1 ? uno : varios}`);
  const total = resultados.length;
  return `${total} ${total === 1 ? "archivo" : "archivos"}: ${partes.join(", ")}.`;
}

/** Texto de la confirmación antes de quitar un lote: que se vea QUÉ lote es. */
export function textoConfirmarQuitarLote(lote: LoteBanco): string {
  return (
    `¿Quitar el lote del ${formatFecha(lote.fecha)} (${lote.moneda} ${formatMonto(lote.importe_total)}, ` +
    `${lote.cantidad_pagos} pagos, ID ${lote.id_transaccion})?\n\n` +
    "Se borran el lote, sus cruces con las facturas y el PDF guardado. No toca SAP. " +
    "Si fue un error, se puede volver a importar el mismo PDF."
  );
}

/** Valida el rango del filtro de lotes. null si está bien. */
export function motivoRangoInvalido(desde: string, hasta: string): string | null {
  if (!desde && !hasta) return "Elegí al menos una fecha.";
  if (desde && hasta && desde > hasta) return "La fecha 'desde' es posterior a 'hasta'.";
  return null;
}

/** Control blando del archivo antes de subir; el backend valida de verdad. */
export function motivoRechazoLote(file: { name: string; size: number; type: string }): string | null {
  if (file.size === 0) return "El archivo está vacío.";
  if (file.size > 10 * 1024 * 1024) return "El archivo pesa más de 10 MB.";
  if (file.type && file.type !== "application/pdf") return "Solo se aceptan archivos PDF.";
  if (!file.type && !file.name.toLowerCase().endsWith(".pdf")) return "Solo se aceptan archivos PDF.";
  return null;
}
